import { getConditionsWithValidUuid, getNewFilterWithoutLongNumber } from "Utils/widgetsUtils"

/**
 * Conditions du widget filtre hiérarchique (bloc « Conditions » de sa configuration).
 *
 * L'arbre du filtre vient d'une requête dédiée (`getReferentialHierarchy`) qui ne connaît pas les
 * conditions. On demande donc au moteur de dashboard — le MÊME qui filtre les autres widgets, donc
 * mêmes opérateurs, dates relatives et champs liés — les identifiants des fiches qui les
 * respectent, et le composant restreint l'arbre à ces fiches (`pruneHierarchyNodes`).
 *
 * ⚠️ Seules les conditions PROPRES du widget sont envoyées, jamais les filtres de la page : un
 * filtre ne doit pas se restreindre lui-même selon les autres filtres posés.
 * ⚠️ Sémantique SQL du moteur : une valeur VIDE ne satisfait jamais « ≠ ». « Statut ≠ Archivé »
 * écarte donc aussi les fiches sans statut (les Branches, sur Mes PCA) — il faut ajouter
 * « OU Statut est vide ». Même comportement que sur tous les autres widgets.
 */

/** Conditions exploitables du widget, ou `null` s'il n'en a pas. */
export function getHierarchyConditions(widgetInfo) {
  if (!widgetInfo?.conditions) return null
  const cleaned = getNewFilterWithoutLongNumber(
    getConditionsWithValidUuid(JSON.parse(JSON.stringify(widgetInfo.conditions)))
  )
  return cleaned?.filters?.length ? cleaned : null
}

/** Corps de requête : les identifiants (colonne idSample) des fiches qui respectent les conditions. */
export function buildHierarchyConditionsBody(conditions, idSampleColumn) {
  return {
    selectAdvanced: [{ fieldUUID: idSampleColumn, outputUUID: "id", aggFunction: "" }],
    groupByFieldUUIDs: [idSampleColumn],
    filter: { operation: "and", filters: [conditions] },
  }
}

/** Identifiants rendus par le moteur (`{ body: { data } }` ou `{ data }`). */
export function readAllowedIds(response) {
  const rows = response?.body?.data ?? response?.data
  if (!Array.isArray(rows)) return null
  return rows.map(row => row?.id).filter(id => id != null && id !== "").map(String)
}

/**
 * Corps de requête des conditions d'un widget filtre hiérarchique, ou `null` s'il n'y en a pas (ou
 * si le widget n'est pas un filtre hiérarchique, ou n'a pas encore de champ). `resolveIdSampleColumn`
 * est injecté : il vit dans le conteneur Widget, qui s'en sert déjà pour la valeur émise.
 */
export function getHierarchyConditionsRequest(widgetInfo, resolveIdSampleColumn) {
  if (widgetInfo?.widgetType !== "hierarchicalFilter") return null
  const fieldKey = widgetInfo?.data?.[0]?.id
  const conditions = getHierarchyConditions(widgetInfo)
  if (!fieldKey || !conditions) return null
  return buildHierarchyConditionsBody(conditions, resolveIdSampleColumn(fieldKey))
}
